import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChatbotService } from "@/lib/chatbotService";

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatbotWidgetProps {
  user?: {
    id: string;
    email: string;
    role: string;
    firstName: string;
    lastName: string;
  };
}

export default function ChatbotWidget({ user }: ChatbotWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      text: "Hi! I'm Sera AI assistant. How can I help you today?",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Generate session ID when chat is opened
  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Generate new session ID when chat is opened
  useEffect(() => {
    if (isOpen && !sessionId) {
      setSessionId(generateSessionId());
    }
  }, [isOpen, sessionId]);

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: messageText,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const response = await ChatbotService.sendMessage(messageText, sessionId, user?.role);
      
      // Log what we received
      console.log('💬 Chatbot response received:', {
        hasError: !!response.error,
        hasResponse: !!response.response,
        responseLength: response.response?.length,
        responsePreview: response.response?.substring(0, 100)
      });
      
      // Extract the response text
      const responseText = response.error || response.response || response.message || "I'm sorry, I couldn't process your request. Please try again.";
      
      // Check if we got the default fallback message
      if (responseText === 'Message received successfully' || responseText.trim() === '') {
        console.warn('⚠️ Received default/empty message, this indicates N8N response format issue');
      }
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: responseText,
        isUser: false,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error("Error sending message to N8N:", error);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: "I'm sorry, I'm having trouble connecting right now. Please try again later.",
        isUser: false,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = () => {
    sendMessage(inputMessage);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        id: "1",
        text: "Hi! I'm Sera your AI assistant. How can I help you today?",
        isUser: false,
        timestamp: new Date(),
      },
    ]);
    setSessionId(generateSessionId());
  };

  return (
    <>
      {/* Floating Chat Bubble */}
      <div className="fixed bottom-8 right-8 z-50">
        <button
          onClick={() => {
            if (!isOpen) {
              // Reset session and messages when opening chat
              setSessionId(generateSessionId());
              setMessages([
                {
                  id: "1",
                  text: "Hi! I'm Sera your AI assistant. How can I help you today?",
                  isUser: false,
                  timestamp: new Date(),
                },
              ]);
            }
            setIsOpen(!isOpen);
          }}
          className="group relative bg-servify-purple p-2 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 overflow-hidden"
          data-testid="chatbot-bubble"
        >
          {/* AI Avatar Image */}
          <div className="ai-avatar-container w-16 h-16 rounded-full overflow-hidden border border-white/40 shadow-2xl bg-white backdrop-blur-sm ai-avatar-glow">
            <div className="w-full h-full rounded-full ring-1 ring-white/50 ring-offset-1 ring-offset-white/20">
              <img 
                src="/ai-avatar-sera-optimized.png" 
                alt="Sera AI Assistant" 
                className="ai-avatar-image w-full h-full object-cover object-center"
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallbackIcon = document.createElement('div');
                  fallbackIcon.innerHTML = '<svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>';
                  target.parentElement?.appendChild(fallbackIcon);
                }}
              />
            </div>
          </div>
          
          {/* Pulse animation */}
          <div className="absolute -inset-2 border-2 border-servify-purple/20 rounded-full animate-ping"></div>
          <div className="absolute -inset-1 border border-servify-purple/30 rounded-full animate-pulse"></div>
          
          {/* Hover tooltip */}
          <div className="absolute bottom-full right-0 mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            <div className="bg-servify-purple text-white text-sm rounded-lg px-4 py-3 whitespace-nowrap shadow-lg">
              Sera – Your AI Assistant  
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-servify-purple"></div>
            </div>
          </div>
        </button>
      </div>

      {/* Chat Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-end p-4">
          <Card className="w-full max-w-md h-[80vh] max-h-[600px] flex flex-col shadow-2xl bg-white">
            <CardHeader className="bg-servify-purple text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mr-3 overflow-hidden border-2 border-white/30 shadow-xl bg-white">
                    <div className="w-full h-full rounded-full ring-1 ring-white/40">
                      <img 
                        src="/ai-avatar-sera-optimized.png" 
                        alt="Sera AI Assistant" 
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                  <div>
                    <CardTitle className="text-white">Sera</CardTitle>
                    <p className="text-xs text-white/80">Online</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearChat}
                    className="text-white hover:bg-white/20 p-2"
                    title="Clear chat"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOpen(false)}
                    className="text-white hover:bg-white/20 p-2"
                    title="Close chat"
                  >
                    <X className="w-4 h-4" />
                  </Button>

                </div>
              </div>
            </CardHeader>
            
            <CardContent className="flex flex-col h-full p-0 overflow-hidden">
              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 bg-white max-h-[calc(100%-80px)]">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex items-start space-x-3 mb-4 ${message.isUser ? 'justify-end' : ''}`}
                  >
                    {!message.isUser && (
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden border-2 border-gray-200 shadow-md bg-white">
                        <div className="w-full h-full rounded-full ring-1 ring-gray-300">
                          <img 
                            src="/ai-avatar-sera-optimized.png" 
                            alt="Sera AI Assistant" 
                            className="w-full h-full object-cover object-center"
                          />
                        </div>
                      </div>
                    )}
                    <div
                      className={`p-3 rounded-lg shadow-sm max-w-xs ${
                        message.isUser
                          ? 'bg-servify-purple text-white ml-auto'
                          : 'bg-white text-gray-900 border border-gray-200'
                      }`}
                    >
                      <p className="text-sm">{message.text}</p>
                      <p className="text-xs opacity-70 mt-1">
                        {message.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </p>
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden border-2 border-gray-200 shadow-md bg-white">
                      <div className="w-full h-full rounded-full ring-1 ring-gray-300">
                        <img 
                          src="/ai-avatar-sera-optimized.png" 
                          alt="Sera AI Assistant" 
                          className="w-full h-full object-cover object-center"
                        />
                      </div>
                    </div>
                    <div className="bg-gray-100 p-3 rounded-lg shadow-sm">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce-delay-1"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce-delay-2"></div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
              
              {/* Input Area */}
              <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0 sticky bottom-0">
                <div className="flex items-center space-x-2">
                  <Input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    className="flex-1 bg-gray-50 border-gray-200 focus:border-servify-purple focus:ring-servify-purple"
                    disabled={isLoading}
                  />
                  <Button
                    onClick={handleSend}
                    disabled={isLoading || !inputMessage.trim()}
                    size="sm"
                    variant="ghost"
                    className="bg-servify-purple hover:bg-servify-purple-dark text-white border-0 !bg-servify-purple !hover:bg-servify-purple-dark"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
} 